import * as PIXI from 'pixi.js';
import tinycolor from '../loaders/tinycolor2';

import TextLineControls from './TextLineControls';
import TextInnerControls from './TextInnerControls';
import TextOuterControls from './TextOuterControls';
import {
  HEAVY,
  LIGHT,
  MEDIUM,
  NONE,
  BLUR_QUALITY,
} from '../constants/DropShadow';
import Color from '../utils/Color';
import SimpleCache from '../utils/SimpleCache';

export default class Text extends PIXI.Container {
  marginTop = 0;
  fontHeight = 0;
  numLines = 0;

  shadowAngle = 0;
  shadowDistance = 0;

  /**
   * Cache key is fontSize + text. Value is rendered text height.
   * @type {SimpleCache.<number>}
   * @private
   */
  static _textHeightCache = new SimpleCache();

  static _isDropShadowEnabled(dropShadow) {
    return (
      dropShadow === LIGHT || dropShadow === MEDIUM || dropShadow === HEAVY
    );
  }

  get text() {
    return this.foreground.text;
  }

  set text(value) {
    this.foreground.text = value;
    if (this.dropShadow) {
      this.dropShadow.text = value;
    }
    this.numLines = value.split('\n').length;
    this._updatePositioning();
  }

  get fontSize() {
    return this.foreground.style.fontSize;
  }

  constructor(app, text = '', ...rest) {
    super();

    this.app = app;

    this.foreground = new PIXI.Text(text, ...rest);
    this.textHighlight = new PIXI.Graphics();
    this.highlightShadow = new PIXI.Graphics();
    this.numLines = text.split('\n').length;
    this.highlightShadow.blendMode = PIXI.BLEND_MODES.MULTIPLY;

    // highlightShadow and textHighlight in theory only need to be rendered if
    // a highlight color is defined and a drop shadow is enabled.
    // The foreground is the text which always needs to be rendered.
    // We later render a text drop shadow if dropShadow is enabled and there is
    // no highlight.
    // highlightShadow and textHighlight could be deferred here if performance
    // problems occur.
    this.addChild(this.highlightShadow, this.textHighlight, this.foreground);

    this.outerControls = new TextOuterControls(this);
    this.lineControls = new TextLineControls(this);
    this.innerControls = new TextInnerControls(this);
  }

  /**
   * Ensure the drop shadow text element exists and is added to the container.
   * @private
   */
  _initializeDropShadow() {
    if (this.dropShadow) {
      return;
    }

    this.dropShadow = new PIXI.Text(this.foreground.text);
    this.dropShadow.anchor.set(0.5, 0.5);
    this.dropShadow.blendMode = PIXI.BLEND_MODES.MULTIPLY;
    this.addChildAt(this.dropShadow, 0);
  }

  /**
   * Remove the existing drop shadow and garbage collect it.
   * @private
   */
  _destroyDropShadow() {
    if (!this.dropShadow) {
      return;
    }

    this.removeChild(this.dropShadow);
    this.dropShadow = null;
  }

  /**
   * Call this from an animation preset's render method in development
   * to see visual guides for debugging text positions
   * @param graphics {PIXI.Graphics}
   */
  drawDebug(graphics) {
    graphics
      .lineStyle(3, 0xff0000)
      .drawRect(
        this.outerControls.x,
        this.outerControls.y,
        this.outerControls.width,
        this.outerControls.height
      );

    graphics
      .lineStyle(2, 0x0000ff)
      .drawRect(
        this.lineControls.x,
        this.lineControls.y,
        this.lineControls.width,
        this.lineControls.height
      );

    graphics
      .lineStyle(1, 0x00ff00)
      .drawRect(
        this.innerControls.x,
        this.innerControls.y,
        this.innerControls.width,
        this.innerControls.height
      );

    if (this.mask) {
      graphics
        .lineStyle(1, 0xff00ff)
        .drawRect(this.mask.x, this.mask.y, this.mask.width, this.mask.height);
    }
  }

  /**
   * Update the text style from the media model
   */
  updateStyle(model, options = {}) {
    const { dropShadow, fontSize, primaryColor, highlightColor, style } = model;
    const { fill = primaryColor, scale = 1, ...styleOverrides } = options;

    const scaledFontSize = this._scaleFontForScreen(fontSize * scale);

    this.foreground.style = {
      ...style,
      fill: tinycolor(fill).toHexString(),
      fontSize: scaledFontSize,
      padding: scaledFontSize * 0.25,
      trim: true,
      ...styleOverrides,
    };
    this._updateFontMetrics();
    this._updateHighlightStyle(highlightColor);
    this._updateHighlightDropShadowStyle(dropShadow);
    this._updateDropShadowStyle(dropShadow);

    this._updatePositioning();
  }

  _scaleFontForScreen(size) {
    return (this.app.screen.height * size) / 300;
  }

  _updateHighlightStyle(highlightColor) {
    this.textHighlight.renderable = false;
    if (highlightColor && this.foreground.text) {
      // Ensure text is present to prevent extra boxes on two-line animations
      const newHighlight = {
        alpha: highlightColor.a,
        color: Color.toNumber(tinycolor(highlightColor)),
      };
      this._updateMarginTop();
      this.textHighlight.clear();
      this.textHighlight.beginFill(newHighlight.color, newHighlight.alpha);
      const { x, y, width, height } = this._getHighlightBounds();
      this.textHighlight.drawRect(x, y, width, height);
      this.textHighlight.endFill();
      this.textHighlight.renderable = true;
    }
  }

  _getHighlightBounds() {
    if (this.foreground.x === this.textHighlight.x) {
      this.foreground.x += this.fontSize / 8;
    }
    return {
      x: this.foreground.x - this.fontSize / 8,
      y: this.foreground.y - this.marginTop,
      width: this.foreground.width + this.fontSize / 4,
      height: this.fontHeight * this.numLines,
    };
  }

  _getHighlightDropShadowStyle(scale, type = NONE) {
    switch (type) {
      case LIGHT:
        return {
          dropShadow: true,
          dropShadowAlpha: 0.3,
          dropShadowBlur: scale * 0.15,
          dropShadowColor: '#A9A9A9',
        };
      case MEDIUM:
        return {
          dropShadow: true,
          dropShadowAlpha: 0.6,
          dropShadowBlur: scale * 0.3,
          dropShadowColor: '#777678',
        };
      case HEAVY:
        return {
          dropShadow: true,
          dropShadowBlur: scale * 0.55,
          dropShadowColor: '#1E1E1E',
        };
      case NONE:
      default:
        return {
          dropShadow: false,
        };
    }
  }

  _updateHighlightDropShadowStyle(dropShadow) {
    this.highlightShadow.renderable = false;

    const dropShadowStyle = this._getHighlightDropShadowStyle(
      this.fontSize,
      dropShadow
    );

    if (dropShadowStyle.dropShadow && this.textHighlight.renderable) {
      // Update highlight shadow
      const shadowColor = Color.toNumber(
        tinycolor(dropShadowStyle.dropShadowColor)
      );
      this.highlightShadow.clear();
      this.highlightShadow.beginFill(
        shadowColor,
        dropShadowStyle.dropShadowAlpha
      );
      this.highlightShadow.drawRect(
        this.textHighlight.x + this.textHighlight.height / 10,
        this.textHighlight.y + this.textHighlight.height / 10,
        this.textHighlight.width,
        this.textHighlight.height
      );
      this.highlightShadow.endFill();
      const blurFilter = new PIXI.filters.BlurFilter();
      blurFilter.blur = dropShadowStyle.dropShadowBlur;
      blurFilter.quality = BLUR_QUALITY;
      this.highlightShadow.filters = [blurFilter];
      this.highlightShadow.renderable = true;
    }
  }

  _getDropShadowStyle(scale, type = NONE) {
    switch (type) {
      case LIGHT:
        return {
          dropShadow: true,
          dropShadowAlpha: 0.9,
          dropShadowAngle: 0.9,
          dropShadowBlur: scale * 0.15,
          dropShadowColor: '#A9A9A9',
          dropShadowDistance: scale * 0.175,
        };
      case MEDIUM:
        return {
          dropShadow: true,
          dropShadowAngle: 0.9,
          dropShadowBlur: scale * 0.3,
          dropShadowColor: '#777678',
          dropShadowDistance: scale * 0.2,
        };
      case HEAVY:
        return {
          dropShadow: true,
          dropShadowAngle: 1.1,
          dropShadowBlur: scale * 0.55,
          dropShadowColor: '#1E1E1E',
          dropShadowDistance: scale * 0.2,
        };
      case NONE:
      default:
        return {
          dropShadow: false,
        };
    }
  }

  _updateDropShadowStyle(dropShadow) {
    if (Text._isDropShadowEnabled(dropShadow)) {
      this._initializeDropShadow();
    } else {
      this._destroyDropShadow();
      return;
    }

    this.dropShadow.renderable = false;

    const fontSize = this.foreground.style.fontSize;
    const dropShadowStyle = this._getDropShadowStyle(fontSize, dropShadow);

    const {
      dropShadowAngle = 0,
      dropShadowDistance = 0,
      ...restShadowStyle
    } = dropShadowStyle;
    const { dropShadowBlur = 0 } = dropShadowStyle;
    const padding =
      fontSize * 0.1 + (dropShadowBlur + dropShadowDistance) * 0.5;

    if (!dropShadowStyle.dropShadow) {
      this.shadowAngle = 0;
      this.shadowDistance = 0;
      this.dropShadow.style = { ...this.foreground.style };
    } else if (!this.textHighlight.renderable) {
      this.dropShadow.style = {
        ...this.foreground.style,
        fill: 'transparent',
        padding,
        ...restShadowStyle,
      };
      this.shadowAngle = dropShadowAngle;
      this.shadowDistance = dropShadowDistance;
      this.dropShadow.renderable = true;
    }
  }

  /**
   * Update the fontHeight value for this font style
   * can add other font metrics here as needed
   * @private
   */
  _updateFontMetrics() {
    const threeLineHeight = this._measureTextHeight('0\n0\n0');
    const twoLineHeight = this._measureTextHeight('0\n0');
    this.fontHeight = threeLineHeight - twoLineHeight;
  }

  _updatePositioning() {
    this._updateMarginTop();
    this._updateDropShadowPosition();
  }

  /**
   * Update the marginTop value for the current text and style.
   * marginTop is the number of pixels between the top of the current text
   * and the maximum possible height for the selected font style.
   * @private
   */
  _updateMarginTop() {
    const currentHeight = this._measureTextHeight(this.text);
    const controlHeight = this._measureTextHeight(',jyg');
    const combinedHeight = this._measureTextHeight(`,jyg\n${this.text}`);
    this.marginTop = combinedHeight - currentHeight - controlHeight;
    this.foreground.y = this.marginTop;
  }

  /**
   * Update dropShadow position within parent container
   * @private
   */
  _updateDropShadowPosition() {
    if (!this.dropShadow) {
      return;
    }

    const letteringCenter = {
      x: this.foreground.x + this.foreground.width / 2,
      y: this.foreground.y + this.foreground.height / 2,
    };
    const shadowDelta = {
      x: this.shadowDistance * Math.cos(this.shadowAngle),
      y: this.shadowDistance * Math.sin(this.shadowAngle),
    };
    this.dropShadow.x = letteringCenter.x + shadowDelta.x;
    this.dropShadow.y = letteringCenter.y + shadowDelta.y;
  }

  _measureTextHeight(value) {
    return Text._textHeightCache.get(
      [this.foreground.style.fontSize, value],
      () => {
        // As text size grows, this becomes a very expensive operation.
        const text = this.foreground.text;
        this.foreground.text = value;
        const textHeight = this.foreground.height;
        this.foreground.text = text;

        return textHeight;
      }
    );
  }
}
